import { PropertyTypes } from "epsm-api-types";
import type { UTCTimestamp } from "lightweight-charts";
import type {
	CreateShapeOptions,
	CustomIndicator,
	EntityId,
	IChartWidgetApi,
	IContext,
	IPineStudyResult,
	LibraryPineStudy,
	PineJS,
	RawStudyMetaInfoId,
	StudyOverrideValueType,
	VisibleTimeRange,
} from "@/charting_library/charting_library";
import {
	formatValue,
	formatValuePercent,
} from "@/components/widgets/helpers/FormattingHelpers";
import { ONE_DAY_IN_S, shiftDateToLocalTimeZone } from "@/lib/utils";
import { callIfExists } from "@/lib/utils/callIfExists/callIfExists";
import { EmQuarterlyReportService } from "../../../../services/EmQuarterlyReportService";
import type { ChartState } from "../../../../types";
import { getIndicatorsService } from "../../datafeed-services/getIndicatorsService";

export const createTimingMomentumText = (
	activeChart: IChartWidgetApi,
	studyId: EntityId,
) => {
	const time = Math.floor(Date.now() / 1000) - ONE_DAY_IN_S * 30 * 11;
	const shapeOptions: CreateShapeOptions<{
		[key: string]: StudyOverrideValueType;
	}> = {
		shape: "text",
		disableSelection: false,
		disableSave: true,
		disableUndo: true,
		lock: false,
		zOrder: "top",
		overrides: {
			color: "black",
			fontsize: 14,
			bold: true,
			italic: true,
		},
		ownerStudyId: studyId,
	};

	activeChart.createShape(
		{
			time: time,
			price: 2,
		},
		{
			...shapeOptions,
			text: "Trend",
		},
	);
	activeChart.createShape(
		{
			time: time,
			price: 1.5,
		},
		{
			...shapeOptions,
			text: "Price Momentum Slope",
		},
	);
	activeChart.createShape(
		{
			time: time,
			price: 1,
		},
		{
			...shapeOptions,
			text: "Relative Market Rank Slope",
		},
	);
	activeChart.createShape(
		{
			time: time,
			price: 0.5,
		},
		{
			...shapeOptions,
			text: "Powerdays",
		},
	);
	activeChart.createShape(
		{
			time: time,
			price: 0,
		},
		{
			...shapeOptions,
			text: "EMA 20/50",
		},
	);
};

// the function accepts visibleTimeRange in miliseconds!
export const drawEMReports = async (
	activeChart: IChartWidgetApi,
	visibleTimeRange: VisibleTimeRange,
	ticker: string,
	emReports: Set<UTCTimestamp>,
) => {
	const response = await EmQuarterlyReportService.create().getQuarterlyReports({
		sort: [{ name: "earning_date", order: 1 }],
		fields: [
			"eps.change",
			"sales.change",
			"fiscal_year",
			"fiscal_quarter",
			"em",
			"earning_date",
		],
		filter: [
			{ name: "ticker", condition: "eq", value: ticker },
			{
				name: "earning_date",
				condition: "gte",
				value: visibleTimeRange.from,
			},
			{
				name: "earning_date",
				condition: "lt",
				value: visibleTimeRange.to,
			},
		],
	});

	const filteredReports =
		response.data?.filter((item) => item.earning_date) ?? [];

	for (const report of filteredReports) {
		const time = Math.trunc(
			shiftDateToLocalTimeZone(report.earning_date!) / 1000,
		) as UTCTimestamp;

		const emText = `EM ${formatValue(report.em?.value, PropertyTypes.NUMBER, { maximumFractionDigits: 0 })}${report.em?.partial ? "*" : ""}`;
		const reportText = `${report.fiscal_year} Q${report.fiscal_quarter}
${emText}

EPS Chg ${formatValuePercent(report.eps?.change)}
Sales Chg ${formatValuePercent(report.sales?.change)}`;

		if (!emReports.has(time)) {
			activeChart.createShape(
				{ time: time, channel: "high" },
				{
					shape: "note",
					text: reportText,
					zOrder: "top",
					disableSelection: false,
					disableSave: true,
					disableUndo: true,
					lock: true,
					overrides: {
						markerColor: "white",
					},
				},
			);
			activeChart.createShape(
				{ time: time, channel: "high" },
				{
					shape: "text",
					text: emText,
					zOrder: "top",
					disableSelection: false,
					disableSave: true,
					disableUndo: true,
					lock: true,
					overrides: {
						color: "white",
					},
				},
			);
			emReports.add(time);
		}
	}
};

export const customIndicatorsGetter = (
	state: ChartState,
	PineJS: PineJS,
): Promise<readonly CustomIndicator[]> => {
	return Promise.resolve(
		[...(state.panels ?? []), ...(state.datasets ?? [])].map((panel) => {
			const panelService = getIndicatorsService(panel);

			return {
				name: panel,
				metainfo: {
					id: `${panel}@tv-basicstudies-1` as RawStudyMetaInfoId,
					description: panel,
					shortDescription: panel,
					isCustomIndicator: true,
					is_price_study: false,
					format: { precision: 2, type: "price" },
					plots: panelService.plots,
					defaults: panelService.defaults,
					palettes: panelService.palettes,
					styles: panelService.styles,
					inputs: [],
				},

				constructor: function (this: LibraryPineStudy<IPineStudyResult>) {
					this.init = function (context: IContext, inputCallback) {
						this._context = context;
						this._input = inputCallback;

						const symbol1 = `${PineJS.Std.ticker(this._context)}:${panel}`;

						this._context.new_sym(
							symbol1,
							panelService.interval
								? panelService.interval
								: PineJS.Std.period(this._context),
						);
					};
					this.main = function (context: IContext, inputCallback) {
						this._context = context;
						this._input = inputCallback;

						this._context.select_sym(1);
						const getPlotsData = callIfExists(panelService.getPlotsData);
						const data = getPlotsData(PineJS, this._context) ?? [];

						return [...data];
					};
				},
			};
		}),
	);
};
